"use client";

import { useT } from "@/lib/i18n/context";
import type { WLatestNewsContent } from "@/lib/api/cms-window-content";
import { wLatestNewsTranslations } from "./WLatestNews.i18n";
import styles from "./WLatestNews.module.css";

/**
 * WLatestNews — renderer okna `w_latest_news` (widget "najnowsze aktualności").
 *
 * **Parytet z legacy** `component/w_latest_news/VIEW/default_modern/
 * w_latest_news_default_modern.imp`, ale wygląd JEST OSOBNY od pełnej listy
 * `c_news` — to kompaktowa lista zajawek (miniaturka + tytuł + skrót) zwieńczona
 * linkiem "zobacz wszystkie" do katalogu źródłowego.
 *
 * **Dane:** `content` = `response_window_data` z `W_LATEST_NEWS_CONTROLLER::
 * getApiContent()`. Katalog źródłowy newsów rozwiązany backendowo z
 * `imp_cms_window.id_catalog` — frontend dostaje gotowe `items` + `href_list`.
 *
 * `short_text` to HTML z edytora (backend robi `html_entity_decode`) —
 * renderowane przez `dangerouslySetInnerHTML`. `name` to plain text.
 */
export function WLatestNews({ content }: WLatestNewsProps) {
  const t = useT(wLatestNewsTranslations);

  if (!content || content.items.length === 0) {
    return <p className={styles.empty}>{t.empty}</p>;
  }

  /* hide_date dziedziczone z katalogu źródłowego c_news - ukrywa datę publikacji. */
  const hideDate = !!content.config?.hide_date;

  return (
    <div className={styles.root}>
      <ul className={styles.list}>
        {content.items.map((item) => (
          <li key={item.id} className={styles.item}>
            <a href={item.href} className={styles.itemLink}>
              {item.picture && (
                <span className={styles.thumb}>
                  {/* Pliki na hoście backendu Imperial — raw <img> (parytet z
                      WPrezentation; next/image wymagałoby remotePatterns). */}
                  <img
                    src={item.picture.thumb_src}
                    alt={item.picture.name}
                    width={item.picture.width ?? undefined}
                    height={item.picture.height ?? undefined}
                    loading="lazy"
                  />
                </span>
              )}
              <span className={styles.body}>
                {!hideDate && item.publication_date && (
                  <time className={styles.date} dateTime={item.publication_date}>
                    {t.formatDate(item.publication_date)}
                  </time>
                )}
                <span className={styles.title}>{item.name}</span>
                {item.short_text && (
                  <span
                    className={styles.excerpt}
                    dangerouslySetInnerHTML={{ __html: item.short_text }}
                  />
                )}
                <span className={styles.readMore}>{t.readMore}</span>
              </span>
            </a>
          </li>
        ))}
      </ul>
      <a href={content.href_list} className={styles.watchAll}>
        {t.watchAll}
      </a>
    </div>
  );
}

export type WLatestNewsProps = {
  content: WLatestNewsContent | null;
};
